import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthGate } from "@/components/AuthGate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listReviews, deleteReview } from "@/lib/reviews.functions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PageContainer } from "@/components/PageContainer";
import { Eyebrow, DisplayHeading } from "@/components/editorial";

export const Route = createFileRoute("/reviews/")({
  head: () => ({
    meta: [
      { title: "Your reviews — Tiger's Eye" },
      { name: "description", content: "All of your Tiger's Eye academic draft reviews in one place." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (<AuthGate><ReviewsList /></AuthGate>),
});

function ReviewsList() {
  const fetchReviews = useServerFn(listReviews);
  const delFn = useServerFn(deleteReview);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["reviews"],
    queryFn: () => fetchReviews(),
  });

  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Review deleted");
      qc.invalidateQueries({ queryKey: ["reviews"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <PageContainer className="min-h-[60vh]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <Eyebrow className="mb-2">Archive</Eyebrow>
          <DisplayHeading level={1}>All reviews</DisplayHeading>
        </div>
        <Link to="/reviews/new"><Button>Start a new review</Button></Link>
      </div>

      {isLoading ? (
        <p className="text-ink-tertiary">Loading…</p>
      ) : !data?.reviews.length ? (
        <div className="border border-rule bg-card p-10 text-center">
          <p className="font-serif text-2xl text-primary">No reviews yet.</p>
          <p className="text-ink-secondary mt-2">Upload your first draft to get started.</p>
          <Link to="/reviews/new"><Button className="mt-6">Start a new review</Button></Link>
        </div>
      ) : (
        <ul className="divide-y divide-rule border border-rule bg-card">
          {data.reviews.map((r) => {
            const intake = (r.intake ?? {}) as Record<string, string>;
            const title = intake.module || intake.course || intake.assessment_type || "Untitled review";
            return (
              <li key={r.id} className="flex items-center justify-between p-4">
                <Link
                  to="/reviews/$reviewId"
                  params={{ reviewId: r.id }}
                  className="flex-1 hover:opacity-80"
                >
                  <p className="font-serif text-lg text-primary">{title}</p>
                  <p className="text-xs text-ink-tertiary">
                    {new Date(r.created_at).toLocaleString("en-GB")} · {r.status}
                    {r.tokens_used ? ` · ${r.tokens_used} tokens` : ""}
                  </p>
                </Link>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (confirm("Delete this review?")) del.mutate(r.id);
                  }}
                >
                  Delete
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </PageContainer>
  );
}
